import { PortableTextBlock } from '@portabletext/react';
import clsx from 'clsx';
import { GatsbyImage } from 'gatsby-plugin-image';
import { useContext } from 'react';
import { twMerge } from 'tailwind-merge';

import { ISanityImgWithAltText } from 'typescriptDefs';

import { addNonBreakingSpaces } from 'helpers/addNonBreakingSpaces';

import { Button } from './Button';
import CustomPortableText from './CustomPortableText';
import { GetKOHOBtn, GetKOHOBtnProps } from './GetKOHOBtn';
import { LocaleContext } from './Layout';
import { H2, H3 } from './Typography';

type Props = {
  btnProps?: Omit<GetKOHOBtnProps, 'children'>;
  className?: string;
  content: PortableTextBlock | PortableTextBlock[] | string;
  ctaText?: string | null;
  ctaUrl?: string;
  image?: ISanityImgWithAltText | null;
  imgClassName?: string;
  labels?: PortableTextBlock | PortableTextBlock[] | null;
};

export const SignUp = ({ btnProps, className, content, ctaText, ctaUrl, image, imgClassName, labels }: Props) => {
  const locale = useContext(LocaleContext);

  return (
    <div
      className={twMerge(
        clsx(
          'flex auto-rows-auto grid-cols-2 flex-col rounded-16 bg-primary-500 text-white md:grid md:rounded-24',
          className,
        ),
      )}
    >
      <div
        className={twMerge(
          clsx(
            'my-auto py-40',
            !!image
              ? 'col-span-1 px-32 md:py-32 max-md:pb-32 md:text-left'
              : 'col-span-2 row-span-2 px-24 text-center md:w-full md:py-64 md:text-center',
            !!labels && 'flex h-full flex-col',
          ),
        )}
      >
        <div
          className={twMerge(
            clsx(
              'mx-auto mb-32 md:mb-48 md:w-3/4 [&_h2]:mb-16 [&_h2]:text-secondary-75 [&_h2_strong]:text-secondary-50 [&_h3]:mb-16 [&_h3]:text-secondary-75 [&_h3_strong]:text-secondary-75 [&_p]:text-lg [&_p]:md:text-xl',
              image && 'mx-0 md:w-full',
            ),
          )}
        >
          {typeof content === 'string' ? (
            image ? (
              <H3 dangerouslySetInnerHTML={{ __html: locale === 'fr' ? addNonBreakingSpaces(content) : content }} />
            ) : (
              <H2 dangerouslySetInnerHTML={{ __html: locale === 'fr' ? addNonBreakingSpaces(content) : content }} />
            )
          ) : (
            <CustomPortableText content={content} />
          )}
        </div>

        {ctaUrl || !btnProps ? (
          <a
            className={twMerge(clsx('block w-full sm:w-fit', !image && 'mx-auto'))}
            target="_blank"
            rel="noopener noreferrer"
            href={ctaUrl}
          >
            <Button
              variant={btnProps?.buttonVariant ?? 'primaryInverse'}
              size={btnProps?.buttonSize}
              className={twMerge('w-full sm:w-fit', btnProps?.buttonClassName)}
              onClick={btnProps?.onClick}
            >
              {ctaText}
            </Button>
          </a>
        ) : (
          <div className={twMerge(clsx('flex items-center justify-center', image && 'justify-start'))}>
            <GetKOHOBtn
              {...btnProps}
              buttonVariant={btnProps?.buttonVariant ?? 'primaryInverse'}
              className={twMerge('w-full sm:w-fit', btnProps.className)}
              buttonClassName={twMerge('w-full sm:w-fit', btnProps.buttonClassName)}
            >
              {ctaText}
            </GetKOHOBtn>
          </div>
        )}

        {!!labels && (
          <div className="flex flex-col gap-8 pt-48 text-white md:mt-auto md:pt-32 lg:flex-row lg:gap-32 md:[&_p]:leading-none">
            <CustomPortableText content={labels} />
          </div>
        )}
      </div>

      {image && (
        <div className="col-span-1 w-full p-32 pt-0 md:pt-32">
          <GatsbyImage
            className={twMerge('h-full max-h-164 w-full rounded-12 xs:max-h-220 sm:max-h-340 md:my-auto', imgClassName)}
            image={image.img.asset.gatsbyImageData}
            alt={image.altText}
            objectFit="cover"
          />
        </div>
      )}
    </div>
  );
};
